import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  BodyDataType,
  storyConversationProps,
  storyDataArrayProps,
} from "./types";
import { WebStoryScreenProps } from "./navigation/types";
import { WEB_SCREENS } from "./constants/screens";
import { TrackingEvents } from "../../Utilities/analytics";
import { storyAssets } from "./constants/assets";
import { trackEvent } from "../../../mixpanel";
import ConfirmModal from "../../../components/ConfirmModal/ConfirmModal";
import { triggerLogout } from "../../../auth/logout";
import Lottie from "lottie-react";
import appSwitchIcon from "../../appAssets/jsons/appSwitchIcon.json";
import {
  useRetrieveStory,
  usePostStoryData,
  useTemplateList,
} from "./useStoryOperations";
import {
  handleTemplateFlags,
  hasStandardBlockAfter,
  isLastBlockPending,
  navigateToStoryBreakScreen,
  getBgImageForBlock,
  LessonPlanQuestion,
} from "./utils/storyHelpers";
import WebIntegratedLessonPlan from "./Components/WebIntegratedLessonPlan";
import YetiIntroModule from "./Components/YetiIntroModule";
import InputContainer from "./Components/InputContainer";
import RenderStoryBackground from "./Components/RenderStoryBackground";
import ObjectViewList from "./Components/ObjectViewList";
import ChapterProgressBar from "./Components/ChapterProgressBar";
import WebConversationBlock from "./Components/WebConversationBlock";
import WebLottiePlayer, { WebLottieSource } from "./Components/WebLottiePlayer";
import YetiCharacter from "../Rooms/components/YetiCharacter/YetiCharacter";
import WebSnowfall from "./Components/WebSnowfall";
import config from "../../../config/environment";
import { resolveContentUrl } from "./utils/resolveContentUrl";
import { BackgroundMusicButton } from "../../components/BackgroundMusic";
import WebShadowButton from "./Components/WebShadowButton";
import { WebLoader } from "../../components/Loader";
import WebObjectView from "./Components/WebObjectView";
import { StoryObjectNavigator } from "./Components/StoryObjectNavigator";
// @ts-ignore
import "./styles/index.css";
import { useNavigate } from "react-router-dom";
import { createWebStoryNavigation } from "./navigation/createWebStoryNavigation";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { setStoryData, clearStoryData } from "../../store/storySlice";

const WebStoryScreen: React.FC<WebStoryScreenProps> = ({
  // navigation,
  routeParams = {},
  isFocused = true,
  storyReducersData: storyReducersDataProp,
  onDispatchSetStoryData,
  onTrackEvent,
  onNavigateToPlanner,
  onShowJourneySplash,
  contentBaseUrl: contentBaseUrlProp,
}) => {
  const dispatch = useAppDispatch();
  const storedStory = useAppSelector((state) => state.story);
  const storyReducersData = useMemo(
    () =>
      storyReducersDataProp ?? {
        data: storedStory.storyData?.data,
        storyId: storedStory.storyId ?? undefined,
      },
    [storyReducersDataProp, storedStory.storyData, storedStory.storyId],
  );
  const contentBaseUrl = contentBaseUrlProp ?? config.contentApiUrl;
  const track = onTrackEvent ?? trackEvent;
  const routerNavigate = useNavigate();
  const navigation = useMemo(
    () => createWebStoryNavigation(routerNavigate),
    [routerNavigate],
  );

  const isSubmitting = React.useRef(false);

  const [submitted, setSubmitted] = useState(false);
  const [showNameView, setShowNameView] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentBlockIdx, setCurrentBlockIdx] = useState(0);
  const [yetiTextIndex, setYetiTextIndex] = useState(0);
  const [yetiIntroArr, setYetiIntroArr] = useState<storyConversationProps[]>();
  const [storyDataArray, setStoryDataArray] = useState<storyDataArrayProps>();
  const [linkedConversationBlock, setLinkedConversationBlock] =
    useState<storyDataArrayProps["data"]["blocks"]>();
  const [userActionsData, setUserActionsData] = useState<storyDataArrayProps>();
  const [pauseMusic, setPauseMusic] = useState(false);
  const [resetCounter, setResetCounter] = useState(false);
  const [showObjectView, setShowObjectView] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [selectedobject, setSelectedobject] = useState<WebLottieSource>("");
  const [enablebagPack, setEnablebagPack] = useState(false);
  const [enableFunbox, setEnableFunbox] = useState(false);
  const [enableMywall, setEnableMywall] = useState(false);
  const [enableJournal, setEnableJournal] = useState(false);
  const [ballonObject, setBallonObject] = useState(false);
  const [isNavigateToJourney, setIsNavigateToJourney] = useState(false);
  const [isMilestoneActivated, setActivateMilestone] = useState(false);
  const [hideProgressBar, setHideProgressBar] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [currentObjectScreen, setCurrentObjectScreen] = useState("");
  const [storyTemplateList, setStoryTemplateList] = useState<
    Array<{ name?: string; type?: string }>
  >([]);
  const [stopAudio, setStopAudio] = useState(false);
  const [lessonPlanQuestion, setLessonPlanQuestion] =
    useState<LessonPlanQuestion | null>(null);
  const [predictionQuestion, setPredictionQuestion] =
    useState<LessonPlanQuestion | null>(null);
  // Milestone found at chapter-break time — used as fallback when ChapterProgressBar
  // can't fire (e.g. the new blocks have no `chapter` field so the bar is hidden).
  const pendingMilestoneRef = useRef<{ milestone: any; bgImage: string; storyId: string; documentId: string } | null>(null);

  const { retrieveStoryData } = useRetrieveStory();
  const { PostStoryData } = usePostStoryData();
  const { retrieveTemplateList } = useTemplateList();

  // Block browser back / forward buttons while on the Rooms screen.
  // pushState adds a sentinel entry so the first Back press lands here;
  // the popstate handler immediately re-pushes to neutralise every subsequent press.
  useEffect(() => {
    window.history.pushState(null, "", window.location.href);
    const handlePopState = () => {
      window.history.pushState(null, "", window.location.href);
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const fetchCurrentIndex = (indexValue: number) => {
    setYetiTextIndex(indexValue);
    if (indexValue === 1) {
      setShowNameView((prev) => !prev);
    }
  };

  const resetObjectsStates = () => {
    setEnableFunbox(false);
    setEnableJournal(false);
    setEnableMywall(false);
    setBallonObject(false);
    setEnablebagPack(false);
  };

  const restartStory = () => {
    setCurrentObjectScreen("");
    setResetCounter(true);
    setCurrentBlockIdx(0);
    setYetiTextIndex(0);
    setShowObjectView(false);
    setShowButton({ state: false, textValue: "" });
    setShowInputText({ state: false, textValue: "" });
    setYetiIntroArr(storyDataArray?.data?.blocks?.[0]?.conversations);
    setTimeout(() => setResetCounter(false), 2000);
  };

  const [showInputText, setShowInputText] = useState({
    state: false,
    textValue: "",
  });
  const [showButtonType, setShowButton] = useState({
    state: false,
    textValue: "",
  });

  const updateGetStoryData = React.useCallback(
    (storyData: storyDataArrayProps | [] | null) => {
      setIsLoading(false);
      if (!(storyData as storyDataArrayProps)?.data) return;
      const sd = storyData as storyDataArrayProps;
      const blocks = sd.data.blocks || [];
      const isHomeBlock =
        blocks?.[0]?.template?.toLowerCase() === "home" &&
        blocks?.[0]?.conversations?.[0]?.object?.[0]?.content
          ?.toLowerCase()
          .includes("this is my home");

      setStoryDataArray(sd);
      dispatch(setStoryData(sd));
      setCurrentBlockIdx(0);

      const blockIndex = isHomeBlock ? 0 : currentBlockIdx;
      setYetiIntroArr(sd?.data?.blocks?.[blockIndex]?.conversations || []);

      if (sd?.data?.isOnboardingCompleted) {
        // navigation.dispatch(
        //   CommonActions.reset({index: 0, routes: [{name: SCREENS.HomeStack}]}),
        // );
      }

      setUserActionsData(
        (sd?.data?.blocks?.[blockIndex]?.userActions ?? []) as never,
      );

      handleTemplateFlags(
        sd?.data?.blocks?.[currentBlockIdx]?.templateObjects || [],
        {
          setEnablebagPack,
          setEnableJournal,
          setEnableMywall,
          setEnableFunbox,
          setBallonObject,
        },
      );

      // ── Integrated Lesson Plan conditions 1 & 2 ──
      const questions: LessonPlanQuestion[] =
        (sd?.data as any)?.questions || [];
      const chapters = (sd?.data as any)?.chapters || [];

      if (
        (chapters[0]?.status === "pending" ||
          chapters[0]?.status === "running") &&
        chapters[0]?.blocks?.[0]?.status === "pending" &&
        sd?.data?.blocks?.[0]?.blockType?.toLowerCase() !== "re-entry block"
      ) {
        const q = questions.find((q) => q.name === "BigQuestion");
        if (q) setLessonPlanQuestion(q);
      } else if (
        (chapters[0]?.status === "pending" ||
          chapters[0]?.status === "running") &&
        chapters[0]?.blocks?.[0]?.status === "completed" &&
        chapters[0]?.blocks?.[1]?.status === "completed" &&
        chapters[0]?.blocks?.[2]?.status === "pending"
      ) {
        const q = questions.find((q) => q.name === "PredictionQuestion");
        if (q) setPredictionQuestion(q);
      }

      setIsLoading(false);
      setTimeout(() => setResetCounter(false), 1000);
    },
    [
      dispatch,
      currentBlockIdx,
      setEnablebagPack,
      setEnableJournal,
      setEnableMywall,
      setEnableFunbox,
      setBallonObject,
    ],
  );

  const handleBlockSubmit = async (
    blockFetchApi?: boolean,
    value?: string,
    linkTo?: string | string[],
    storyID?: string,
    blockID?: number,
  ) => {
    if (isSubmitting.current) return;
    isSubmitting.current = true;
    setPredictionQuestion(null);
    console.log(blockFetchApi, "blockFetchApi");
    setIsLoading(true);
    setLinkedConversationBlock(undefined);
    setShowInputText({ state: false, textValue: "" });
    setShowButton({ state: false, textValue: "" });

    const linkToArray = Array.isArray(linkTo)
      ? linkTo
      : linkTo
        ? [linkTo]
        : null;

    let bodyData: BodyDataType = {
      storyId: storyDataArray?.storyId ?? storyDataArray?.insertedId ?? storyID,
      blocks: linkToArray
        ? linkToArray.map((value) => ({
            id: parseInt(value as string, 10),
            status: "completed",
          }))
        : [
            {
              id:
                storyDataArray?.data?.blocks?.[currentBlockIdx]?.id ??
                blockID ??
                0,
              status: "completed",
            },
          ],
    };
    type TemplateObject = { name: string };
    type ActionType = { linkTo: string };

    type UpdatedBodyJsDataType = {
      template: { name: string; status: boolean };
    };
    let updatedBodyJsData: UpdatedBodyJsDataType | null = null;
    (userActionsData as ActionType[] | undefined)?.forEach((action) => {
      const data = (
        storyDataArray?.data?.blocks?.[currentBlockIdx]?.templateObjects as
          | TemplateObject[]
          | undefined
      )?.filter((t) => t?.name === action?.linkTo);
      if (data && data[0] && data[0].name) {
        updatedBodyJsData = { template: { name: data[0].name, status: true } };
      }
    });
    if (
      updatedBodyJsData &&
      typeof updatedBodyJsData === "object" &&
      (updatedBodyJsData as UpdatedBodyJsDataType).template?.name
    ) {
      bodyData = {
        ...bodyData,
        ...(typeof updatedBodyJsData === "object" &&
        !Array.isArray(updatedBodyJsData)
          ? updatedBodyJsData
          : {}),
      };
    }

    if (value && value.length > 0) {
      bodyData = { ...bodyData, name: value };
    }

    console.log("block submit bodyData", bodyData);
    // "Yes I do": single parentId — find linked block locally, no API call
    if (linkToArray?.length === 1 && !blockFetchApi) {
      const singleId = linkToArray[0];
      console.log("linkTo block submit", singleId);
      const linked = storyDataArray?.data?.blocks?.filter(
        (block: { documentId?: string; id?: any }) =>
          block.documentId === singleId || block?.id === singleId,
      );
      console.log(linked, "linkedlinkedlinkedlinked");
      if (linked?.length) {
        setIsLoading(false);
        isSubmitting.current = false;
        setLinkedConversationBlock(linked);
        return;
      }
    }

    const isConvoLastBlock = storyDataArray?.data?.blocks?.length === 1;
    track(TrackingEvents.story_block_complete, {
      story_name: storyDataArray?.data?.name,
      block_name: storyDataArray?.data?.blocks?.[0]?.name,
      block_type: storyDataArray?.data?.blocks?.[0]?.blockType,
      heart_block_type: storyDataArray?.data?.blocks?.[0]?.heartBlockType,
      is_last_block_of_story: isConvoLastBlock,
    });

    let isLastChapterblock = isLastBlockPending(
      storyDataArray?.data?.chapters as never,
    );
    console.log("isLastChapterblock", isLastChapterblock);

    try {
      if (!isLastChapterblock) {
        const isStandardBlock = hasStandardBlockAfter(
          storyDataArray?.data?.blocks,
          storyDataArray?.data?.chapters as never,
        );
        if (!isStandardBlock) {
          isLastChapterblock = true;
        }
      }
    } catch {
      // keep default
    }

    try {
      const postDataVal = await PostStoryData(bodyData);

      if (postDataVal instanceof Error || !postDataVal?.data) {
        setIsLoading(false);
        isSubmitting.current = false;
        return;
      }

      // ── Detect which chapter (if any) just completed ────────────────────
      // Compare pre-submit vs post-submit chapter statuses. This is more
      // reliable than isLastChapterblock/chapter-field checks, which can fail
      // when chapter status is "pending" instead of "running", or when the
      // block's `chapter` field is absent/falsy.
      const preChapters: any[] = (storyDataArray?.data as any)?.chapters ?? [];
      const postChapters: any[] = (postDataVal?.data as any)?.chapters ?? [];
      const newlyCompletedChapter = postChapters.find((postChap: any) => {
        const preChap = preChapters.find((pc: any) => pc.id === postChap.id);
        return postChap.status === "completed" && preChap?.status !== "completed";
      });

      console.log("[ChapterBreak] newlyCompletedChapter:", newlyCompletedChapter);
      console.log("[ChapterBreak] postMilestones:", (postDataVal?.data as any)?.milestones);

      if (newlyCompletedChapter) {
        const respMilestones: any[] = (postDataVal?.data as any)?.milestones ?? [];
        const m = respMilestones.find(
          (ms: any) => ms.chapter === newlyCompletedChapter.id,
        );
        console.log("[ChapterBreak] matchingMilestone:", m);
        if (m) {
          pendingMilestoneRef.current = {
            milestone: m,
            bgImage: storyDataArray?.data?.blocks?.[0]?.bgImage ?? "",
            storyId: String(postDataVal?.storyId ?? ""),
            documentId: String((postDataVal?.data as any)?.documentId ?? ""),
          };
        } else {
          // Milestone not found by chapter.id — try fallback: use last milestone
          const lastMilestone = respMilestones[respMilestones.length - 1];
          console.warn("[ChapterBreak] milestone not found by id, using last:", lastMilestone);
          if (lastMilestone) {
            pendingMilestoneRef.current = {
              milestone: lastMilestone,
              bgImage: storyDataArray?.data?.blocks?.[0]?.bgImage ?? "",
              storyId: String(postDataVal?.storyId ?? ""),
              documentId: String((postDataVal?.data as any)?.documentId ?? ""),
            };
          }
        }
      }

      // Handle blocks.length === 0 (story/chapter fully done): try fast-path
      // navigate first; pendingMilestoneRef above is the fallback if it fails.
      if ((postDataVal?.data?.blocks ?? []).length === 0) {
        const currentBgImage = storyDataArray?.data?.blocks?.[0]?.bgImage;
        const chaps = (postDataVal?.data as any)?.chapters || [];
        const qs: LessonPlanQuestion[] =
          (postDataVal?.data as any)?.questions || [];
        let reflectionQuestion: LessonPlanQuestion | undefined;
        if (
          chaps[2]?.status === "completed" &&
          chaps[2]?.blocks?.every((b: any) => b.status === "completed")
        ) {
          reflectionQuestion = qs.find((q) => q.name === "ReflectionQuestion");
        }
        const navigated = (postDataVal?.data as any)?.milestones?.length > 0;
        if (navigated) {
          navigateToStoryBreakScreen(
            postDataVal,
            routerNavigate,
            currentBgImage,
            reflectionQuestion,
          );
          setIsLoading(false);
          isSubmitting.current = false;
          return;
        }
        // milestones empty — fall through so pendingMilestoneRef handles it
      }

      console.log("isConvoLastBlock", isConvoLastBlock);
      if (isConvoLastBlock) {
        dispatch(clearStoryData());
      } else {
        dispatch(setStoryData(postDataVal));
      }

      if (
        newlyCompletedChapter ||
        (isLastChapterblock && storyDataArray?.data?.blocks?.[0]?.chapter) ||
        isConvoLastBlock
      ) {
        setActivateMilestone(true);
      }

      resetObjectsStates();
      setYetiIntroArr([]);
      setYetiTextIndex(0);

      if (isConvoLastBlock) {
        setStoryDataArray({
          ...postDataVal,
          data: {
            ...postDataVal?.data,
            blocks: storyDataArray?.data?.blocks,
          },
        });
      } else {
        setStoryDataArray(undefined);
        // routerNavigate("/web/WebStoryBreakScreen", {
        //   state: { milestones: postDataVal?.data?.milestones },
        // });
        // navigateToStoryBreakScreen(postDataVal, routerNavigate);
      }

      setIsLoading(false);
      updateGetStoryData([] as never);

      const route =
        storyDataArray?.data?.blocks?.[currentBlockIdx]?.route ?? "";
      if (route.toLowerCase().includes("story")) {
        return;
      }

      const doodleLink =
        storyDataArray?.data?.blocks?.[currentBlockIdx]?.userActions?.[0]
          ?.linkTo;
      if (doodleLink?.toLowerCase().includes("doodling")) {
        return;
      }

      if (route.toLowerCase().includes("chat")) {
        return;
      }

      setCurrentBlockIdx(0);
      // if (blockFetchApi) {
      //   return;
      // }

      setIsLoading(true);

      setResetCounter(true);
      if (isConvoLastBlock) {
        setStoryDataArray({
          ...postDataVal,
          data: {
            ...postDataVal?.data,
            blocks: storyDataArray?.data.blocks,
          },
        });
      } else {
        setStoryDataArray(undefined);
      }
      if (
        storyDataArray?.data?.id === 1 &&
        storyDataArray?.data?.blocks.length === 1
      ) {
        setIsNavigateToJourney(true);
      }

      setCurrentBlockIdx(0);
      setUserActionsData(undefined);
      setTimeout(() => {
        updateGetStoryData(postDataVal);
        isSubmitting.current = false;
      }, 1000);
    } catch {
      setIsLoading(false);
      isSubmitting.current = false;
    }
  };

  const handleSubmit = (value: string) => {
    track(TrackingEvents.name_entered, { name: value });
    setSubmitted(true);
    setShowNameView(false);
    handleBlockSubmit(false, value);
  };

  const getStoryData = useCallback(
    async (storyUrl?: string) => {
      setCurrentBlockIdx(0);
      setCurrentObjectScreen("");
      const templateList = await retrieveTemplateList();
      setStoryTemplateList(templateList?.data ?? []);

      if (storyReducersData?.data && !routeParams?.refreshStory) {
        const storyData = await retrieveStoryData(
          `?storyId=${storyReducersData?.storyId}`,
        );
        updateGetStoryData(storyData);
      } else {
        setIsLoading(true);
        const endpoint =
          storyUrl && !String(storyUrl).startsWith("?")
            ? routeParams?.isDocumentId
              ? `?documentId=${storyUrl}`
              : `?storyId=${storyUrl}`
            : storyUrl;
        const storyData = await retrieveStoryData(endpoint);
        updateGetStoryData(storyData);
      }
    },
    [
      retrieveTemplateList,
      storyReducersData?.data,
      storyReducersData?.storyId,
      routeParams?.refreshStory,
      routeParams?.isDocumentId,
      retrieveStoryData,
      updateGetStoryData,
    ],
  );

  useEffect(() => {
    if (!isFocused) {
      setPauseMusic(true);
      setStoryDataArray(undefined);
      dispatch(clearStoryData());
      return;
    }
    restartStory();
    setActivateMilestone(false);
    pendingMilestoneRef.current = null;
    if (routeParams?.storyId) {
      dispatch(clearStoryData());
      getStoryData(routeParams.storyId);
    } else {
      getStoryData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFocused]);

  useEffect(() => {
    if (!yetiIntroArr?.length) {
      return;
    }

    const blockUserActions =
      storyDataArray?.data?.blocks?.[currentBlockIdx]?.userActions ?? [];
    const storyUserActions =
      (userActionsData as unknown as Array<{
        component?: string;
        buttonText?: string;
        linkTo?: string;
      }>) ?? blockUserActions;

    const timer = setTimeout(() => {
      storyUserActions?.forEach((action) => {
        if (action?.linkTo === "backPackScreen") {
          setEnablebagPack(true);
          setCurrentObjectScreen(WEB_SCREENS.MagicBagPack);
          setSelectedobject("Magic Backpack");
        }
        if (action?.linkTo === "travelPlan") {
          setEnableJournal(true);
          setCurrentObjectScreen(WEB_SCREENS.TravelPlanScreen);
          setSelectedobject("Planner");
        }
        if (action?.linkTo === "wallScreen") {
          setEnableMywall(true);
          setCurrentObjectScreen(WEB_SCREENS.MyWall);
          setSelectedobject("My Wall");
        }
        if (action?.linkTo === "funBoxScreen") {
          setEnableFunbox(true);
          setCurrentObjectScreen(WEB_SCREENS.FunBoxScreen);
          setSelectedobject("Fun box");
        }
        if (action?.linkTo === "ballonScreen") {
          setBallonObject(true);
          setCurrentObjectScreen(WEB_SCREENS.MyJourneyScreen);
        }
      });
    }, 1000);

    if (yetiIntroArr.length - 1 === yetiTextIndex) {
      storyUserActions?.forEach((action) => {
        if (action?.component?.toLowerCase() === "block.text-input") {
          setShowInputText({ state: true, textValue: action.buttonText ?? "" });
        }
        if (action?.component?.toLowerCase() === "block.button-input") {
          setShowButton({ state: true, textValue: action.buttonText ?? "" });
        }
      });
    }
    return () => clearTimeout(timer);
  }, [
    yetiTextIndex,
    yetiIntroArr,
    userActionsData,
    storyDataArray,
    currentBlockIdx,
  ]);

  useEffect(() => {
    if (isNavigateToJourney && onShowJourneySplash) {
      onShowJourneySplash(() => {
        setIsNavigateToJourney(false);
        dispatch(clearStoryData());
      });
    }
  }, [isNavigateToJourney, onShowJourneySplash, dispatch]);

  // Fallback: if ChapterProgressBar couldn't fire onMilestoneClick (hidden by
  // isLoading or missing `chapter` field on new blocks), navigate directly after
  // the same 1-second animation window. Cleared if onMilestoneClick fires first
  // (it calls setActivateMilestone(false) which resets isMilestoneActivated).
  useEffect(() => {
    console.log("[MilestoneFallback] effect fired, isMilestoneActivated:", isMilestoneActivated, "pendingMilestone:", pendingMilestoneRef.current);
    if (!isMilestoneActivated || !pendingMilestoneRef.current) return;
    const pending = pendingMilestoneRef.current;
    console.log("[MilestoneFallback] starting 1500ms timer for milestone:", pending.milestone);
    const timer = setTimeout(() => {
      if (!pendingMilestoneRef.current) {
        console.log("[MilestoneFallback] timer fired but onMilestoneClick already handled it");
        return;
      }
      console.log("[MilestoneFallback] navigating to break screen with:", pending);
      pendingMilestoneRef.current = null;
      setActivateMilestone(false);
      setStopAudio(true);
      routerNavigate("/web/WebStoryBreakScreen", {
        state: {
          milestoneData: pending.milestone,
          storyId: pending.storyId,
          bgImage: pending.bgImage,
          documentId: pending.documentId,
        },
      });
    }, 1500);
    return () => clearTimeout(timer);
  }, [isMilestoneActivated, routerNavigate]);

  const updateApiCall = (linkId?: string | string[], updateParent?: string) => {
    // console.log(
    //   updateParent,
    //   "jhxgkjhsgadkf",
    //   updateParent?.length,
    //   "updateParent",
    //   linkId,
    //   "updateApiCall linkId",
    // );
    handleBlockSubmit(
      updateParent && updateParent?.length > 0 ? true : false,
      "",
      linkId,
    );
    // handleBlockSubmit(updateParent === "updateParent", "", linkId);
  };

  // if (storyDataArray?.data?.blocks?.length === 0) {
  //   navigateToStoryBreakScreen(storyDataArray, routerNavigate);
  //   return null;
  // }

  const doLogout = () => {
    dispatch(clearStoryData());
    setPauseMusic(true);
    triggerLogout("manual");
  };

  const handleLogout = () => setShowLogoutConfirm(true);

  const templateName =
    storyDataArray?.data?.blocks?.[currentBlockIdx]?.template?.toLowerCase() ??
    "";
  const isHome = ["home", "snoozing", "homepageopendoor"].includes(
    templateName,
  );

  const isDuoTemplate = storyTemplateList?.find(
    (item) =>
      item.name?.toLowerCase() === templateName &&
      item.type?.toLowerCase() === "duo",
  );
  if (isDuoTemplate) {
    const conversationBlock =
      linkedConversationBlock?.[0] ?? storyDataArray?.data?.blocks?.[0];

    return (
      <div style={{ position: "fixed", inset: 0, overflow: "hidden" }}>
        <WebConversationBlock
          isLoading={isLoading}
          hideProgressBar={setHideProgressBar}
          conversationValue={conversationBlock as Record<string, unknown>}
          allBlocksData={storyDataArray?.data as Record<string, unknown>}
          updateApiCall={updateApiCall}
          isMilestoneActivated={isMilestoneActivated}
          stopAudio={stopAudio || !!lessonPlanQuestion}
          // navigation={navigate}
          isFocused={isFocused}
          contentBaseUrl={contentBaseUrl}
          onTrackEvent={track}
          onShowJourneySplash={onShowJourneySplash}
          isOnboardingCompleted={!!storyDataArray?.isOnboardingCompleted}
          showPredictionBtn={!!predictionQuestion}
          onPredictionBtnPress={() => {
            setLessonPlanQuestion(predictionQuestion);
            setPredictionQuestion(null);
          }}
        />
        {lessonPlanQuestion && (
          <WebIntegratedLessonPlan
            lessonPlanQuestion={lessonPlanQuestion}
            onStart={() => setLessonPlanQuestion(null)}
          />
        )}
        {!isLoading &&
          !hideProgressBar &&
          !lessonPlanQuestion &&
          Boolean(storyDataArray?.data?.blocks?.[0]?.chapter) && (
            <ChapterProgressBar
              chapters={(storyDataArray?.data?.chapters ?? []).map(
                (chapter) => ({
                  ...chapter,
                  status:
                    chapter.status === "pending" ||
                    chapter.status === "completed"
                      ? chapter.status
                      : "pending",
                  blocks: chapter.blocks.map((block) => ({
                    ...block,
                    status:
                      block.status === "pending" || block.status === "completed"
                        ? block.status
                        : "pending",
                  })),
                }),
              )}
              isMilestoneActivated={isMilestoneActivated}
              milestones={storyDataArray?.data?.milestones as never}
              currentBlockId={
                storyDataArray?.data?.blocks?.[currentBlockIdx]?.id ?? 0
              }
              onMilestoneClick={(milestone) => {
                pendingMilestoneRef.current = null; // cancel fallback timer
                setActivateMilestone(false);
                setStopAudio(true);
                routerNavigate("/web/WebStoryBreakScreen", {
                  state: {
                    milestoneData: milestone,
                    storyId: storyDataArray?.storyId,
                    bgImage: getBgImageForBlock(
                      storyDataArray?.data?.blocks?.[0],
                    ),
                    documentId: storyDataArray?.data?.documentId,
                  },
                });
                // navigation.navigate(WEB_SCREENS.ApplicationNavigatorStack, {
                //   screen: WEB_SCREENS.StoryBreakScreen,
                //   params: {
                //     milestoneData: milestone,
                //     storyId: storyDataArray?.storyId,
                //     bgImage: getBgImageForBlock(
                //       storyDataArray?.data?.blocks?.[0],
                //     ),
                //     documentId: storyDataArray?.data?.documentId,
                //   },
                // });
              }}
            />
          )}
      </div>
    );
  }

  const yetiCharUri = resolveContentUrl(
    storyDataArray?.data?.blocks?.[currentBlockIdx]?.conversations?.[
      yetiTextIndex
    ]?.object?.[0]?.characterImage,
    contentBaseUrl,
  );

  return (
    <div className="web-story-screen">
      <img src={storyAssets.introIpad} alt="" className="web-story-bg-image" />
      <WebSnowfall maxSize={2} />

      {storyDataArray?.isOnboardingCompleted && (
        <button
          type="button"
          className="web-story-journey-btn"
          onClick={
            () => {}
            // navigation.navigate(WEB_SCREENS.ApplicationNavigatorStack, {
            //   screen: WEB_SCREENS.MyJourneyScreen,
            // })
          }
        >
          <WebLottiePlayer
            source={storyAssets.lottie.journeyIcon}
            className="web-story-journey-icon"
            autoPlay={false}
            loop={false}
          />
        </button>
      )}

      <RenderStoryBackground
        screenName={templateName}
        backgrounduri={resolveContentUrl(
          getBgImageForBlock(storyDataArray?.data?.blocks?.[currentBlockIdx]),
          contentBaseUrl,
        )}
        useBackgroundImage={isHome}
      >
        <YetiCharacter
          characterImage={yetiCharUri || null}
          className={`web-story-yeti-character ${isHome ? "web-story-yeti-home" : ""}`}
        />
        <div className="web-story-yeti-container">
          {yetiIntroArr && yetiIntroArr.length > 0 && (
            <YetiIntroModule
              resetCounter={resetCounter}
              submittedVal={submitted}
              updateCurrentIndex={fetchCurrentIndex}
              yetiIntroText={yetiIntroArr}
              containerClassName={isHome ? "web-yeti-home" : ""}
              contentBaseUrl={contentBaseUrl}
            />
          )}
          {showInputText.state && (
            <div className="web-story-text-area">
              <InputContainer
                updateInputValue={(value) => {
                  setPauseMusic(false);
                  handleSubmit(value);
                }}
              />
            </div>
          )}
          {showButtonType.state && (
            <div className="web-story-explore-btn">
              <WebShadowButton
                title={showButtonType.textValue}
                onPress={() => {
                  setPauseMusic(true);
                  if (currentObjectScreen) {
                    StoryObjectNavigator({
                      currentObjectScreen,
                      storyDataArray: storyDataArray!,
                      routeParams,
                      navigation,
                      setSelectedObject: setSelectedobject,
                      setShowObjectView,
                      setIsNavigateToJourney,
                      setYetiIntroArr: () => setYetiIntroArr([]),
                      handleBlockSubmit: () => handleBlockSubmit(true),
                      onNavigateToPlanner,
                      onTrackEvent: track,
                    });
                  } else {
                    handleBlockSubmit();
                  }
                }}
              />
            </div>
          )}
        </div>
        {storyDataArray && (
          <ObjectViewList
            enablebagPack={enablebagPack}
            enableJournal={enableJournal}
            enableFunbox={enableFunbox}
            enableMywall={enableMywall}
            ballonObject={ballonObject}
            navigation={navigation}
            routeParams={routeParams}
            storyDataArray={storyDataArray}
            setSelectedobject={setSelectedobject}
            setShowObjectView={setShowObjectView}
            setIsNavigateToJourney={setIsNavigateToJourney}
            setYetiIntroArr={() => setYetiIntroArr([])}
            handleBlockSubmit={() => handleBlockSubmit(true)}
            currentObjectScreen={currentObjectScreen}
            setDisplayName={setDisplayName}
            onNavigateToPlanner={onNavigateToPlanner}
            onTrackEvent={track}
          />
        )}
      </RenderStoryBackground>

      <div className="web-story-toolbar">
        {!(
          storyDataArray?.data?.id === 1 &&
          (storyDataArray?.data?.blocks?.[0]?.order ?? 0) < 3
        ) &&
          (showInputText.state || showButtonType.state) && (
            <button
              type="button"
              className="web-story-toolbar-btn"
              onClick={() => {
                track(TrackingEvents.story_replayed);
                restartStory();
              }}
            >
              <WebLottiePlayer
                source={storyAssets.lottie.replayIcon}
                className="web-story-replay-icon"
                autoPlay={false}
                loop={false}
              />
            </button>
          )}
        <div className="web-story-toolbar-btn web-story-music-btn">
          <BackgroundMusicButton
            audioUrl={storyDataArray?.data?.bgMusic}
            defaultPlaying={!pauseMusic}
            glowIcon={storyDataArray?.isOnboardingCompleted === false}
            isOnBoardingDone={storyDataArray?.isOnboardingCompleted === true}
            onPlayStateChange={(playing) => setPauseMusic(!playing)}
          />
        </div>
        <div className={`rooms__ctrl-btn`}>
          <button
            className="rooms__ctrl-btn"
            aria-label="Go to Dashboard"
            onClick={() => routerNavigate("/dashboard")}
          >
            <Lottie
              animationData={appSwitchIcon}
              loop={false}
              autoplay={false}
              className="rooms__ctrl-lottie"
            />
          </button>
        </div>
        <button
          type="button"
          className="web-story-toolbar-btn web-story-menu-btn"
          onClick={() => handleLogout()}
          aria-label="Menu"
        >
          <WebLottiePlayer
            source={storyAssets.lottie.logoutIcon}
            className="web-convo-menu-icon"
            autoPlay={false}
            loop={false}
          />
        </button>
      </div>

      {showObjectView && (
        <div className="web-story-object-overlay">
          <img
            src={storyAssets.raysBg}
            alt=""
            className="web-story-object-overlay-rays"
          />
          <div className="web-story-object-overlay-content">
            <WebObjectView
              sourceImg={selectedobject}
              displayText={displayName}
              onObjectPress={() => {
                setSelectedobject("");
                setShowObjectView(false);
              }}
              objectClassName="web-story-object-overlay-lottie"
            />
          </div>
        </div>
      )}

      {isLoading && <WebLoader />}

      <ConfirmModal
        isOpen={showLogoutConfirm}
        title="Confirm Logout"
        message="Are you sure you want to log out?"
        confirmText="Log out"
        cancelText="Cancel"
        onConfirm={() => {
          setShowLogoutConfirm(false);
          doLogout();
        }}
        onCancel={() => setShowLogoutConfirm(false)}
      />
    </div>
  );
};

export default WebStoryScreen;
